// Garden planting data for Mapleton, UT (Zone 6b, ~5200ft elevation)
// Last frost: ~May 10, First frost: ~Oct 5
// All week numbers are approximate and relative to calendar year

export type StorageMethod =
  | 'freeze'
  | 'can'
  | 'dry/dehydrate'
  | 'pickle/ferment'
  | 'root cellar'
  | 'blanch & freeze'
  | 'sauce/puree & freeze'
  | 'pesto & freeze'
  | 'eat fresh only'
  | 'cure & store'
  | 'hang dry'
  | 'braid & hang'
  | 'jam/preserve';

export interface PlantInfo {
  name: string;
  category: 'vegetable' | 'herb' | 'fruit' | 'flower';
  // Week numbers (1-52) for key dates
  indoorStart: [number, number] | null;
  transplant: [number, number] | null;
  directSow: [number, number] | null;
  harvestStart: number;
  harvestEnd: number;
  daysToMaturity: [number, number];
  companions: string[];
  avoid: string[];
  notes: string;
  successionPlanting: boolean;
  coldHardy: boolean;
  storage: StorageMethod[];
  storageNotes: string;
  lifecycle: 'annual' | 'perennial' | 'biennial';
}

// Helper: week numbers for reference
// Jan=1-4, Feb=5-9, Mar=10-13, Apr=14-17, May=18-22, Jun=23-26
// Jul=27-30, Aug=31-35, Sep=36-39, Oct=40-43, Nov=44-48, Dec=49-52
// Last frost ~May 10 = week 19, First frost ~Oct 5 = week 40

export const plants: PlantInfo[] = [
  // === WARM SEASON ===
  {
    name: 'Tomato',
    category: 'vegetable',
    indoorStart: [8, 10],
    transplant: [20, 22],
    directSow: null,
    harvestStart: 28,
    harvestEnd: 40,
    daysToMaturity: [60, 85],
    companions: ['Basil', 'Carrot', 'Marigold', 'Parsley'],
    avoid: ['Fennel', 'Brassicas', 'Corn'],
    notes: 'Start indoors 6-8 weeks before last frost. Harden off before transplanting.',
    successionPlanting: false,
    coldHardy: false,
    storage: ['can', 'freeze', 'sauce/puree & freeze', 'dry/dehydrate'],
    storageNotes: 'Can whole or as sauce. Freeze roasted or diced. Sun-dry or dehydrate.',
    lifecycle: 'annual',
  },
  {
    name: 'Pepper (Bell)',
    category: 'vegetable',
    indoorStart: [7, 9],
    transplant: [21, 23],
    directSow: null,
    harvestStart: 29,
    harvestEnd: 40,
    daysToMaturity: [60, 90],
    companions: ['Basil', 'Tomato', 'Carrot', 'Onion'],
    avoid: ['Fennel', 'Brassicas'],
    notes: 'Needs warm soil (65°F+). Start indoors 8-10 weeks before last frost.',
    successionPlanting: false,
    coldHardy: false,
    storage: ['freeze', 'dry/dehydrate', 'pickle/ferment'],
    storageNotes: 'Dice and freeze raw. Roast and freeze. Dehydrate for powder.',
    lifecycle: 'annual',
  },
  {
    name: 'Pepper (Hot)',
    category: 'vegetable',
    indoorStart: [6, 8],
    transplant: [21, 23],
    directSow: null,
    harvestStart: 29,
    harvestEnd: 40,
    daysToMaturity: [70, 100],
    companions: ['Basil', 'Tomato', 'Carrot'],
    avoid: ['Fennel', 'Brassicas'],
    notes: 'Start earlier than bells — they need more time. Jalapeño, serrano, etc.',
    successionPlanting: false,
    coldHardy: false,
    storage: ['dry/dehydrate', 'freeze', 'pickle/ferment', 'sauce/puree & freeze'],
    storageNotes: 'String and hang dry. Freeze whole. Pickle or make hot sauce.',
    lifecycle: 'annual',
  },
  {
    name: 'Cucumber',
    category: 'vegetable',
    indoorStart: [14, 16],
    transplant: [20, 22],
    directSow: [20, 24],
    harvestStart: 27,
    harvestEnd: 38,
    daysToMaturity: [50, 70],
    companions: ['Beans (Bush)', 'Corn', 'Peas', 'Sunflower', 'Dill'],
    avoid: ['Potato', 'Aromatic herbs'],
    notes: 'Sensitive to cold. Wait until soil is 60°F+.',
    successionPlanting: true,
    coldHardy: false,
    storage: ['pickle/ferment'],
    storageNotes: 'Best as pickles (fermented or vinegar). Doesn\'t freeze or store fresh well.',
    lifecycle: 'annual',
  },
  {
    name: 'Zucchini',
    category: 'vegetable',
    indoorStart: [14, 16],
    transplant: [20, 22],
    directSow: [20, 23],
    harvestStart: 26,
    harvestEnd: 38,
    daysToMaturity: [45, 60],
    companions: ['Corn', 'Beans (Bush)', 'Nasturtium', 'Marigold'],
    avoid: ['Potato'],
    notes: 'Very productive. 2-3 plants is usually plenty for a family.',
    successionPlanting: false,
    coldHardy: false,
    storage: ['freeze', 'dry/dehydrate'],
    storageNotes: 'Shred and freeze for bread/muffins. Dehydrate slices. Doesn\'t store fresh.',
    lifecycle: 'annual',
  },
  {
    name: 'Winter Squash',
    category: 'vegetable',
    indoorStart: [14, 16],
    transplant: [20, 22],
    directSow: [20, 23],
    harvestStart: 35,
    harvestEnd: 40,
    daysToMaturity: [80, 110],
    companions: ['Corn', 'Beans (Pole)', 'Marigold'],
    avoid: ['Potato'],
    notes: 'Butternut, acorn, spaghetti squash. Needs space! Part of Three Sisters.',
    successionPlanting: false,
    coldHardy: false,
    storage: ['root cellar', 'sauce/puree & freeze'],
    storageNotes: 'Cure in sun 1-2 weeks, then root cellar 3-6 months. Puree and freeze.',
    lifecycle: 'annual',
  },
  {
    name: 'Corn',
    category: 'vegetable',
    indoorStart: null,
    transplant: null,
    directSow: [20, 24],
    harvestStart: 30,
    harvestEnd: 36,
    daysToMaturity: [60, 100],
    companions: ['Beans (Pole)', 'Winter Squash', 'Zucchini', 'Cucumber', 'Pumpkin'],
    avoid: ['Tomato'],
    notes: 'Plant in blocks (not rows) for pollination. Three Sisters: corn + beans + squash.',
    successionPlanting: true,
    coldHardy: false,
    storage: ['freeze', 'can', 'dry/dehydrate'],
    storageNotes: 'Blanch and cut off cob, then freeze. Can as whole kernel or cream style.',
    lifecycle: 'annual',
  },
  {
    name: 'Beans (Pole)',
    category: 'vegetable',
    indoorStart: null,
    transplant: null,
    directSow: [20, 26],
    harvestStart: 28,
    harvestEnd: 39,
    daysToMaturity: [55, 70],
    companions: ['Corn', 'Winter Squash', 'Zucchini', 'Cucumber', 'Carrot'],
    avoid: ['Onion', 'Garlic', 'Fennel'],
    notes: 'Part of Three Sisters. Fix nitrogen in soil. Keep picking for continuous harvest.',
    successionPlanting: true,
    coldHardy: false,
    storage: ['blanch & freeze', 'can', 'dry/dehydrate'],
    storageNotes: 'Blanch 3 min then freeze. Pressure can. Let dry on vine for dry beans.',
    lifecycle: 'annual',
  },
  {
    name: 'Beans (Bush)',
    category: 'vegetable',
    indoorStart: null,
    transplant: null,
    directSow: [20, 28],
    harvestStart: 27,
    harvestEnd: 38,
    daysToMaturity: [45, 60],
    companions: ['Corn', 'Cucumber', 'Carrot', 'Potato'],
    avoid: ['Onion', 'Garlic', 'Fennel'],
    notes: 'Great for succession planting every 2-3 weeks.',
    successionPlanting: true,
    coldHardy: false,
    storage: ['blanch & freeze', 'can', 'dry/dehydrate'],
    storageNotes: 'Blanch 3 min then freeze. Pressure can. Let dry on vine for dry beans.',
    lifecycle: 'annual',
  },
  {
    name: 'Pumpkin',
    category: 'vegetable',
    indoorStart: [14, 16],
    transplant: [20, 22],
    directSow: [20, 23],
    harvestStart: 36,
    harvestEnd: 41,
    daysToMaturity: [90, 120],
    companions: ['Corn', 'Beans (Pole)', 'Marigold'],
    avoid: ['Potato'],
    notes: 'Needs lots of space. Plant by June 1 for Halloween pumpkins.',
    successionPlanting: false,
    coldHardy: false,
    storage: ['root cellar', 'sauce/puree & freeze'],
    storageNotes: 'Cure in sun, store cool & dry 2-3 months. Roast and puree to freeze.',
    lifecycle: 'annual',
  },
  {
    name: 'Watermelon',
    category: 'fruit',
    indoorStart: [12, 14],
    transplant: [21, 23],
    directSow: null,
    harvestStart: 32,
    harvestEnd: 38,
    daysToMaturity: [70, 90],
    companions: ['Corn', 'Marigold', 'Nasturtium'],
    avoid: ['Potato'],
    notes: 'Short season varieties best for UT. Use black plastic mulch to warm soil.',
    successionPlanting: false,
    coldHardy: false,
    storage: ['eat fresh only'],
    storageNotes: 'Eat fresh. Can freeze cubes for smoothies but texture changes.',
    lifecycle: 'annual',
  },
  {
    name: 'Cantaloupe',
    category: 'fruit',
    indoorStart: [12, 14],
    transplant: [21, 23],
    directSow: null,
    harvestStart: 31,
    harvestEnd: 37,
    daysToMaturity: [65, 85],
    companions: ['Corn', 'Sunflower'],
    avoid: ['Potato'],
    notes: 'Start indoors for Mapleton. Needs warm soil.',
    successionPlanting: false,
    coldHardy: false,
    storage: ['eat fresh only'],
    storageNotes: 'Eat fresh. Can freeze cubes for smoothies.',
    lifecycle: 'annual',
  },
  {
    name: 'Eggplant',
    category: 'vegetable',
    indoorStart: [7, 9],
    transplant: [21, 23],
    directSow: null,
    harvestStart: 30,
    harvestEnd: 39,
    daysToMaturity: [65, 85],
    companions: ['Beans (Bush)', 'Pepper (Bell)', 'Tomato'],
    avoid: ['Fennel'],
    notes: 'Loves heat. Start indoors 8-10 weeks before last frost.',
    successionPlanting: false,
    coldHardy: false,
    storage: ['freeze', 'dry/dehydrate'],
    storageNotes: 'Roast or grill first, then freeze. Doesn\'t store fresh.',
    lifecycle: 'annual',
  },
  {
    name: 'Tomatillo',
    category: 'vegetable',
    indoorStart: [8, 10],
    transplant: [20, 22],
    directSow: null,
    harvestStart: 28,
    harvestEnd: 39,
    daysToMaturity: [60, 80],
    companions: ['Basil', 'Pepper (Hot)', 'Carrot'],
    avoid: ['Fennel', 'Dill'],
    notes: 'Need 2+ plants for pollination. Husks split when ripe. Great for salsa verde.',
    successionPlanting: false,
    coldHardy: false,
    storage: ['freeze', 'can', 'sauce/puree & freeze'],
    storageNotes: 'Roast and freeze. Make salsa verde and can or freeze.',
    lifecycle: 'annual',
  },
  {
    name: 'Ground Cherry',
    category: 'fruit',
    indoorStart: [8, 10],
    transplant: [20, 22],
    directSow: null,
    harvestStart: 30,
    harvestEnd: 40,
    daysToMaturity: [65, 80],
    companions: ['Basil', 'Parsley'],
    avoid: [],
    notes: 'Husked fruit, sweet tropical flavor. Drops when ripe — lay down mulch to catch.',
    successionPlanting: false,
    coldHardy: false,
    storage: ['jam/preserve', 'eat fresh only'],
    storageNotes: 'Eat fresh or make jam. In husks they keep a few weeks at room temp.',
    lifecycle: 'annual',
  },
  {
    name: 'Okra',
    category: 'vegetable',
    indoorStart: [10, 12],
    transplant: [21, 23],
    directSow: null,
    harvestStart: 29,
    harvestEnd: 38,
    daysToMaturity: [50, 65],
    companions: ['Pepper (Bell)', 'Eggplant', 'Basil'],
    avoid: [],
    notes: 'Needs heat. Soak seeds overnight before planting. Pick pods young (3-4 inches).',
    successionPlanting: false,
    coldHardy: false,
    storage: ['blanch & freeze', 'pickle/ferment', 'dry/dehydrate'],
    storageNotes: 'Blanch and freeze. Pickle. Dehydrate for soups.',
    lifecycle: 'annual',
  },

  // === COOL SEASON ===
  {
    name: 'Peas',
    category: 'vegetable',
    indoorStart: null,
    transplant: null,
    directSow: [12, 16],
    harvestStart: 22,
    harvestEnd: 28,
    daysToMaturity: [55, 70],
    companions: ['Carrot', 'Corn', 'Cucumber', 'Beans (Bush)', 'Turnip'],
    avoid: ['Onion', 'Garlic'],
    notes: 'One of the first things to plant! Can go out as soon as soil is workable. Fall sow week 31-33.',
    successionPlanting: true,
    coldHardy: true,
    storage: ['blanch & freeze', 'dry/dehydrate'],
    storageNotes: 'Blanch 2 min then freeze — best method. Shell and dry for split peas.',
    lifecycle: 'annual',
  },
  {
    name: 'Lettuce',
    category: 'vegetable',
    indoorStart: [8, 10],
    transplant: [14, 17],
    directSow: [13, 17],
    harvestStart: 18,
    harvestEnd: 26,
    daysToMaturity: [30, 60],
    companions: ['Carrot', 'Radish', 'Strawberry', 'Onion'],
    avoid: [],
    notes: 'Bolts in heat. Great for spring and fall. Fall sow week 32-36.',
    successionPlanting: true,
    coldHardy: true,
    storage: ['eat fresh only'],
    storageNotes: 'Doesn\'t store. Eat fresh. Extend season with cold frames.',
    lifecycle: 'annual',
  },
  {
    name: 'Spinach',
    category: 'vegetable',
    indoorStart: null,
    transplant: null,
    directSow: [12, 16],
    harvestStart: 18,
    harvestEnd: 24,
    daysToMaturity: [35, 50],
    companions: ['Strawberry', 'Peas', 'Beans (Bush)'],
    avoid: [],
    notes: 'Very cold hardy. Direct sow early spring. Fall crop: sow week 32-36.',
    successionPlanting: true,
    coldHardy: true,
    storage: ['blanch & freeze'],
    storageNotes: 'Blanch 1-2 min, squeeze out water, freeze flat in bags.',
    lifecycle: 'annual',
  },
  {
    name: 'Kale',
    category: 'vegetable',
    indoorStart: [8, 10],
    transplant: [14, 17],
    directSow: [13, 17],
    harvestStart: 20,
    harvestEnd: 44,
    daysToMaturity: [50, 70],
    companions: ['Beet', 'Onion', 'Potato'],
    avoid: ['Tomato', 'Strawberry'],
    notes: 'Frost improves flavor. Can harvest into late fall. Fall sow week 28-32.',
    successionPlanting: true,
    coldHardy: true,
    storage: ['blanch & freeze', 'dry/dehydrate'],
    storageNotes: 'Blanch and freeze. Dehydrate for kale chips or powder for smoothies.',
    lifecycle: 'annual',
  },
  {
    name: 'Broccoli',
    category: 'vegetable',
    indoorStart: [8, 10],
    transplant: [15, 18],
    directSow: null,
    harvestStart: 23,
    harvestEnd: 28,
    daysToMaturity: [55, 80],
    companions: ['Beet', 'Carrot', 'Onion', 'Dill'],
    avoid: ['Tomato', 'Strawberry', 'Pepper (Bell)'],
    notes: 'Start indoors 6-8 wks before last frost. Fall crop: start indoors week 22-24.',
    successionPlanting: false,
    coldHardy: true,
    storage: ['blanch & freeze'],
    storageNotes: 'Cut into florets, blanch 3 min, ice bath, freeze. Keeps 12 months.',
    lifecycle: 'annual',
  },
  {
    name: 'Cauliflower',
    category: 'vegetable',
    indoorStart: [8, 10],
    transplant: [15, 18],
    directSow: null,
    harvestStart: 24,
    harvestEnd: 28,
    daysToMaturity: [55, 80],
    companions: ['Beet', 'Onion', 'Dill'],
    avoid: ['Tomato', 'Strawberry'],
    notes: 'Trickier than broccoli — sensitive to heat and cold swings.',
    successionPlanting: false,
    coldHardy: true,
    storage: ['blanch & freeze', 'pickle/ferment'],
    storageNotes: 'Blanch and freeze florets. Pickle for giardiniera.',
    lifecycle: 'annual',
  },
  {
    name: 'Cabbage',
    category: 'vegetable',
    indoorStart: [8, 10],
    transplant: [15, 18],
    directSow: null,
    harvestStart: 24,
    harvestEnd: 30,
    daysToMaturity: [60, 100],
    companions: ['Beet', 'Onion', 'Dill', 'Celery'],
    avoid: ['Tomato', 'Strawberry'],
    notes: 'Fall crop possible — start indoors week 22-24.',
    successionPlanting: false,
    coldHardy: true,
    storage: ['root cellar', 'pickle/ferment'],
    storageNotes: 'Root cellar 3-4 months. Ferment into sauerkraut or kimchi.',
    lifecycle: 'biennial',
  },
  {
    name: 'Brussels Sprouts',
    category: 'vegetable',
    indoorStart: [10, 12],
    transplant: [18, 20],
    directSow: null,
    harvestStart: 36,
    harvestEnd: 44,
    daysToMaturity: [80, 110],
    companions: ['Beet', 'Carrot', 'Onion', 'Dill'],
    avoid: ['Tomato', 'Strawberry'],
    notes: 'Long season crop. Frost sweetens the sprouts. Harvest bottom-up.',
    successionPlanting: false,
    coldHardy: true,
    storage: ['blanch & freeze'],
    storageNotes: 'Blanch 4 min then freeze. Can keep on stalk in cold garage a few weeks.',
    lifecycle: 'biennial',
  },
  {
    name: 'Kohlrabi',
    category: 'vegetable',
    indoorStart: [8, 10],
    transplant: [14, 17],
    directSow: [14, 18],
    harvestStart: 20,
    harvestEnd: 26,
    daysToMaturity: [40, 60],
    companions: ['Beet', 'Onion', 'Cucumber'],
    avoid: ['Tomato', 'Strawberry'],
    notes: 'Fast-growing brassica. Harvest when bulb is 2-3 inches. Great raw or roasted.',
    successionPlanting: true,
    coldHardy: true,
    storage: ['root cellar', 'blanch & freeze'],
    storageNotes: 'Root cellar 2-3 months. Dice, blanch, and freeze.',
    lifecycle: 'biennial',
  },
  {
    name: 'Turnip',
    category: 'vegetable',
    indoorStart: null,
    transplant: null,
    directSow: [13, 17],
    harvestStart: 19,
    harvestEnd: 26,
    daysToMaturity: [30, 60],
    companions: ['Peas', 'Onion'],
    avoid: ['Potato'],
    notes: 'Fast grower. Eat roots and greens. Great fall crop too — sow week 32-35.',
    successionPlanting: true,
    coldHardy: true,
    storage: ['root cellar', 'blanch & freeze'],
    storageNotes: 'Root cellar 3-4 months. Dice and blanch greens to freeze.',
    lifecycle: 'biennial',
  },
  {
    name: 'Carrot',
    category: 'vegetable',
    indoorStart: null,
    transplant: null,
    directSow: [14, 20],
    harvestStart: 24,
    harvestEnd: 40,
    daysToMaturity: [60, 80],
    companions: ['Tomato', 'Lettuce', 'Onion', 'Peas', 'Rosemary'],
    avoid: ['Dill'],
    notes: 'Direct sow only — doesn\'t transplant well. Keep soil moist for germination.',
    successionPlanting: true,
    coldHardy: true,
    storage: ['root cellar', 'blanch & freeze', 'can'],
    storageNotes: 'Root cellar in damp sand 4-6 months. Blanch and freeze. Pressure can.',
    lifecycle: 'biennial',
  },
  {
    name: 'Beet',
    category: 'vegetable',
    indoorStart: null,
    transplant: null,
    directSow: [14, 20],
    harvestStart: 22,
    harvestEnd: 38,
    daysToMaturity: [45, 65],
    companions: ['Onion', 'Lettuce', 'Cabbage', 'Broccoli'],
    avoid: ['Beans (Pole)'],
    notes: 'Eat the greens too! Succession sow every 3 weeks. Fall sow week 28-32.',
    successionPlanting: true,
    coldHardy: true,
    storage: ['root cellar', 'can', 'pickle/ferment'],
    storageNotes: 'Root cellar in damp sand 3-5 months. Can or pickle.',
    lifecycle: 'biennial',
  },
  {
    name: 'Radish',
    category: 'vegetable',
    indoorStart: null,
    transplant: null,
    directSow: [12, 17],
    harvestStart: 16,
    harvestEnd: 22,
    daysToMaturity: [22, 35],
    companions: ['Carrot', 'Lettuce', 'Peas', 'Cucumber'],
    avoid: [],
    notes: 'Fastest veggie! Great row marker. Fall sow week 32-36.',
    successionPlanting: true,
    coldHardy: true,
    storage: ['eat fresh only', 'pickle/ferment'],
    storageNotes: 'Eat fresh — doesn\'t store well. Quick pickle for short-term keeping.',
    lifecycle: 'annual',
  },
  {
    name: 'Onion',
    category: 'vegetable',
    indoorStart: [4, 6],
    transplant: [14, 17],
    directSow: [14, 17],
    harvestStart: 28,
    harvestEnd: 34,
    daysToMaturity: [90, 120],
    companions: ['Carrot', 'Beet', 'Lettuce', 'Tomato', 'Pepper (Bell)'],
    avoid: ['Beans (Pole)', 'Beans (Bush)', 'Peas'],
    notes: 'Use long-day varieties for UT. Start indoors in Jan for biggest bulbs.',
    successionPlanting: false,
    coldHardy: true,
    storage: ['cure & store', 'dry/dehydrate'],
    storageNotes: 'Cure 2-3 weeks in dry shade, then store cool & dry 3-6 months. Dehydrate.',
    lifecycle: 'biennial',
  },
  {
    name: 'Leek',
    category: 'vegetable',
    indoorStart: [4, 6],
    transplant: [14, 17],
    directSow: null,
    harvestStart: 28,
    harvestEnd: 44,
    daysToMaturity: [90, 130],
    companions: ['Carrot', 'Celery', 'Onion'],
    avoid: ['Beans (Bush)', 'Peas'],
    notes: 'Start indoors early like onions. Hill soil around stems for white shanks. Very cold hardy.',
    successionPlanting: false,
    coldHardy: true,
    storage: ['freeze', 'root cellar'],
    storageNotes: 'Slice and freeze. Can leave in ground with heavy mulch into winter.',
    lifecycle: 'biennial',
  },
  {
    name: 'Green Onion',
    category: 'vegetable',
    indoorStart: null,
    transplant: null,
    directSow: [12, 28],
    harvestStart: 18,
    harvestEnd: 38,
    daysToMaturity: [30, 60],
    companions: ['Carrot', 'Lettuce', 'Tomato'],
    avoid: ['Beans (Bush)', 'Peas'],
    notes: 'Easy and fast. Succession sow every 2-3 weeks for continuous supply.',
    successionPlanting: true,
    coldHardy: true,
    storage: ['freeze', 'dry/dehydrate', 'eat fresh only'],
    storageNotes: 'Chop and freeze. Dehydrate. Best eaten fresh.',
    lifecycle: 'annual',
  },
  {
    name: 'Garlic',
    category: 'vegetable',
    indoorStart: null,
    transplant: null,
    directSow: [40, 44],
    harvestStart: 27,
    harvestEnd: 30,
    daysToMaturity: [240, 270],
    companions: ['Tomato', 'Pepper (Bell)', 'Beet'],
    avoid: ['Beans (Pole)', 'Beans (Bush)', 'Peas'],
    notes: 'Plant in FALL (Oct-Nov). Hardneck varieties best for cold climates.',
    successionPlanting: false,
    coldHardy: true,
    storage: ['cure & store', 'braid & hang', 'dry/dehydrate'],
    storageNotes: 'Cure 3-4 weeks, then store 6-9 months. Braid softneck. Dehydrate for powder.',
    lifecycle: 'annual',
  },
  {
    name: 'Potato',
    category: 'vegetable',
    indoorStart: null,
    transplant: null,
    directSow: [15, 18],
    harvestStart: 26,
    harvestEnd: 36,
    daysToMaturity: [70, 120],
    companions: ['Beans (Bush)', 'Corn', 'Marigold'],
    avoid: ['Tomato', 'Cucumber', 'Pumpkin', 'Winter Squash'],
    notes: 'Plant seed potatoes 2-4 weeks before last frost. Hill as they grow.',
    successionPlanting: false,
    coldHardy: true,
    storage: ['root cellar'],
    storageNotes: 'Cure 1-2 weeks at room temp, then root cellar 4-6 months at 35-40°F.',
    lifecycle: 'annual',
  },
  {
    name: 'Sweet Potato',
    category: 'vegetable',
    indoorStart: [8, 10],
    transplant: [22, 24],
    directSow: null,
    harvestStart: 36,
    harvestEnd: 40,
    daysToMaturity: [90, 120],
    companions: ['Beans (Bush)', 'Corn'],
    avoid: ['Tomato'],
    notes: 'Start slips from tubers indoors. Needs long warm season — use black plastic mulch.',
    successionPlanting: false,
    coldHardy: false,
    storage: ['cure & store', 'root cellar'],
    storageNotes: 'Cure at 80-85°F for 1-2 weeks, then store at 55-60°F for 6+ months.',
    lifecycle: 'annual',
  },
  {
    name: 'Swiss Chard',
    category: 'vegetable',
    indoorStart: [10, 12],
    transplant: [16, 18],
    directSow: [16, 22],
    harvestStart: 24,
    harvestEnd: 40,
    daysToMaturity: [50, 60],
    companions: ['Beans (Bush)', 'Onion', 'Cabbage'],
    avoid: [],
    notes: 'Heat tolerant greens. Cut-and-come-again harvesting. Beautiful in the garden.',
    successionPlanting: false,
    coldHardy: true,
    storage: ['blanch & freeze'],
    storageNotes: 'Blanch stems 3 min, leaves 1 min, then freeze.',
    lifecycle: 'biennial',
  },
  {
    name: 'Celery',
    category: 'vegetable',
    indoorStart: [6, 8],
    transplant: [16, 18],
    directSow: null,
    harvestStart: 28,
    harvestEnd: 38,
    daysToMaturity: [85, 120],
    companions: ['Cabbage', 'Leek', 'Tomato', 'Beans (Bush)'],
    avoid: [],
    notes: 'Needs consistent moisture and rich soil. Long season — start indoors very early.',
    successionPlanting: false,
    coldHardy: true,
    storage: ['freeze', 'dry/dehydrate'],
    storageNotes: 'Dice and freeze for cooking (texture changes). Dehydrate for seasoning.',
    lifecycle: 'biennial',
  },
  {
    name: 'Asparagus',
    category: 'vegetable',
    indoorStart: null,
    transplant: [14, 17],
    directSow: null,
    harvestStart: 17,
    harvestEnd: 23,
    daysToMaturity: [730, 1095],
    companions: ['Tomato', 'Parsley', 'Basil'],
    avoid: ['Onion', 'Garlic'],
    notes: 'Perennial — plant crowns once, harvest for 15-20 years. Don\'t harvest first 2 years.',
    successionPlanting: false,
    coldHardy: true,
    storage: ['blanch & freeze', 'pickle/ferment'],
    storageNotes: 'Blanch 2-3 min and freeze. Pickle spears. Best eaten fresh.',
    lifecycle: 'perennial',
  },
  {
    name: 'Rhubarb',
    category: 'vegetable',
    indoorStart: null,
    transplant: [14, 17],
    directSow: null,
    harvestStart: 17,
    harvestEnd: 26,
    daysToMaturity: [365, 730],
    companions: ['Garlic', 'Onion'],
    avoid: [],
    notes: 'Perennial. Plant crowns. Don\'t harvest first year. Leaves are toxic — stalks only.',
    successionPlanting: false,
    coldHardy: true,
    storage: ['freeze', 'jam/preserve'],
    storageNotes: 'Chop and freeze raw. Make jam, pie filling, or compote to can.',
    lifecycle: 'perennial',
  },

  // === FRUIT ===
  {
    name: 'Strawberry',
    category: 'fruit',
    indoorStart: null,
    transplant: [14, 17],
    directSow: null,
    harvestStart: 23,
    harvestEnd: 30,
    daysToMaturity: [60, 90],
    companions: ['Lettuce', 'Spinach', 'Onion', 'Borage'],
    avoid: ['Brassicas', 'Fennel'],
    notes: 'June-bearing or everbearing. Pinch flowers first year for stronger plants.',
    successionPlanting: false,
    coldHardy: true,
    storage: ['freeze', 'jam/preserve', 'dry/dehydrate'],
    storageNotes: 'Freeze whole on sheet pan then bag. Make jam. Dehydrate for snacks.',
    lifecycle: 'perennial',
  },
  {
    name: 'Raspberry',
    category: 'fruit',
    indoorStart: null,
    transplant: [14, 17],
    directSow: null,
    harvestStart: 27,
    harvestEnd: 38,
    daysToMaturity: [365, 730],
    companions: ['Garlic', 'Marigold'],
    avoid: ['Potato', 'Tomato'],
    notes: 'Perennial cane fruit. Summer or fall-bearing varieties. Prune spent canes.',
    successionPlanting: false,
    coldHardy: true,
    storage: ['freeze', 'jam/preserve', 'dry/dehydrate'],
    storageNotes: 'Freeze on sheet pan then bag. Make jam. Very fragile fresh.',
    lifecycle: 'perennial',
  },

  // === HERBS ===
  {
    name: 'Basil',
    category: 'herb',
    indoorStart: [10, 14],
    transplant: [20, 22],
    directSow: [21, 24],
    harvestStart: 24,
    harvestEnd: 38,
    daysToMaturity: [50, 75],
    companions: ['Tomato', 'Pepper (Bell)', 'Pepper (Hot)'],
    avoid: ['Sage'],
    notes: 'Classic tomato companion. Pinch flowers for bushier growth.',
    successionPlanting: true,
    coldHardy: false,
    storage: ['pesto & freeze', 'dry/dehydrate', 'freeze'],
    storageNotes: 'Make pesto and freeze in ice cube trays. Dry or freeze leaves.',
    lifecycle: 'annual',
  },
  {
    name: 'Cilantro',
    category: 'herb',
    indoorStart: null,
    transplant: null,
    directSow: [13, 17],
    harvestStart: 18,
    harvestEnd: 24,
    daysToMaturity: [30, 45],
    companions: ['Tomato', 'Lettuce', 'Beans (Bush)'],
    avoid: ['Fennel'],
    notes: 'Bolts fast in heat. Succession sow every 2-3 weeks. Fall sow week 32-34.',
    successionPlanting: true,
    coldHardy: true,
    storage: ['freeze', 'dry/dehydrate'],
    storageNotes: 'Freeze in oil in ice cube trays. Loses flavor when dried. Save seeds as coriander.',
    lifecycle: 'annual',
  },
  {
    name: 'Dill',
    category: 'herb',
    indoorStart: null,
    transplant: null,
    directSow: [15, 22],
    harvestStart: 22,
    harvestEnd: 36,
    daysToMaturity: [40, 60],
    companions: ['Cucumber', 'Broccoli', 'Cabbage', 'Lettuce'],
    avoid: ['Carrot', 'Tomato'],
    notes: 'Attracts beneficial insects. Let some go to seed for self-sowing.',
    successionPlanting: true,
    coldHardy: true,
    storage: ['freeze', 'dry/dehydrate'],
    storageNotes: 'Freeze fronds. Dry seeds for dill seed. Use fresh for pickles.',
    lifecycle: 'annual',
  },
  {
    name: 'Parsley',
    category: 'herb',
    indoorStart: [8, 10],
    transplant: [16, 18],
    directSow: [16, 20],
    harvestStart: 22,
    harvestEnd: 40,
    daysToMaturity: [70, 90],
    companions: ['Tomato', 'Carrot', 'Corn'],
    avoid: [],
    notes: 'Slow to germinate (2-4 weeks). Biennial — will flower second year.',
    successionPlanting: false,
    coldHardy: true,
    storage: ['freeze', 'dry/dehydrate'],
    storageNotes: 'Freeze in oil in ice cube trays. Dries well — retains flavor.',
    lifecycle: 'biennial',
  },
  {
    name: 'Rosemary',
    category: 'herb',
    indoorStart: [6, 10],
    transplant: [20, 22],
    directSow: null,
    harvestStart: 22,
    harvestEnd: 40,
    daysToMaturity: [80, 120],
    companions: ['Carrot', 'Beans (Bush)', 'Cabbage'],
    avoid: [],
    notes: 'Perennial in milder areas but may need winter protection in Mapleton.',
    successionPlanting: false,
    coldHardy: false,
    storage: ['dry/dehydrate', 'freeze'],
    storageNotes: 'Dries very well — hang bunches. Freeze sprigs. Nearly evergreen if overwintered.',
    lifecycle: 'perennial',
  },
  {
    name: 'Thyme',
    category: 'herb',
    indoorStart: [8, 10],
    transplant: [18, 20],
    directSow: null,
    harvestStart: 22,
    harvestEnd: 40,
    daysToMaturity: [70, 90],
    companions: ['Cabbage', 'Tomato', 'Eggplant'],
    avoid: [],
    notes: 'Perennial. Very hardy in Mapleton. Low-growing ground cover. Bees love it.',
    successionPlanting: false,
    coldHardy: true,
    storage: ['dry/dehydrate', 'freeze'],
    storageNotes: 'Dries exceptionally well — hang bunches. Freeze sprigs.',
    lifecycle: 'perennial',
  },
  {
    name: 'Oregano',
    category: 'herb',
    indoorStart: [8, 10],
    transplant: [18, 20],
    directSow: null,
    harvestStart: 22,
    harvestEnd: 40,
    daysToMaturity: [60, 80],
    companions: ['Tomato', 'Pepper (Bell)', 'Beans (Bush)'],
    avoid: [],
    notes: 'Perennial. Spreads vigorously — consider containing. Flavor best before flowering.',
    successionPlanting: false,
    coldHardy: true,
    storage: ['dry/dehydrate', 'freeze'],
    storageNotes: 'Dries better than fresh — more concentrated flavor. Hang bunches.',
    lifecycle: 'perennial',
  },
  {
    name: 'Sage',
    category: 'herb',
    indoorStart: [8, 10],
    transplant: [18, 20],
    directSow: null,
    harvestStart: 22,
    harvestEnd: 40,
    daysToMaturity: [75, 100],
    companions: ['Cabbage', 'Carrot', 'Rosemary', 'Broccoli'],
    avoid: ['Basil', 'Cucumber'],
    notes: 'Perennial. Don\'t overwater. Woody shrub after a couple years.',
    successionPlanting: false,
    coldHardy: true,
    storage: ['dry/dehydrate', 'freeze'],
    storageNotes: 'Dries very well. Freeze leaves. Essential for Thanksgiving!',
    lifecycle: 'perennial',
  },
  {
    name: 'Mint',
    category: 'herb',
    indoorStart: null,
    transplant: [16, 20],
    directSow: null,
    harvestStart: 20,
    harvestEnd: 40,
    daysToMaturity: [60, 90],
    companions: ['Cabbage', 'Tomato'],
    avoid: [],
    notes: 'Perennial. VERY invasive — always grow in a pot or contained bed.',
    successionPlanting: false,
    coldHardy: true,
    storage: ['dry/dehydrate', 'freeze'],
    storageNotes: 'Dry for tea. Freeze in ice cube trays for drinks. Grows back every year.',
    lifecycle: 'perennial',
  },
  {
    name: 'Chives',
    category: 'herb',
    indoorStart: [8, 10],
    transplant: [16, 18],
    directSow: [16, 20],
    harvestStart: 18,
    harvestEnd: 40,
    daysToMaturity: [60, 90],
    companions: ['Carrot', 'Tomato', 'Strawberry'],
    avoid: ['Beans (Bush)', 'Peas'],
    notes: 'Perennial. Divide clumps every few years. Flowers are edible and attract pollinators.',
    successionPlanting: false,
    coldHardy: true,
    storage: ['freeze', 'dry/dehydrate'],
    storageNotes: 'Chop and freeze — best method. Drying works but loses some flavor.',
    lifecycle: 'perennial',
  },

  // === FLOWERS (companion/useful) ===
  {
    name: 'Sunflower',
    category: 'flower',
    indoorStart: null,
    transplant: null,
    directSow: [19, 24],
    harvestStart: 32,
    harvestEnd: 40,
    daysToMaturity: [55, 80],
    companions: ['Cucumber', 'Corn', 'Beans (Pole)', 'Zucchini'],
    avoid: ['Potato'],
    notes: 'Attracts pollinators. Provides shade for cool-season crops. Harvest seeds when head droops.',
    successionPlanting: true,
    coldHardy: false,
    storage: ['dry/dehydrate'],
    storageNotes: 'Cut heads, hang to dry, then shell seeds. Roast and salt for snacking.',
    lifecycle: 'annual',
  },
  {
    name: 'Marigold',
    category: 'flower',
    indoorStart: [10, 12],
    transplant: [19, 22],
    directSow: [20, 24],
    harvestStart: 24,
    harvestEnd: 40,
    daysToMaturity: [45, 65],
    companions: ['Tomato', 'Pepper (Bell)', 'Beans (Bush)', 'Cucumber', 'Zucchini'],
    avoid: [],
    notes: 'Repels nematodes and many pests. Plant throughout the garden. Deadhead for more blooms.',
    successionPlanting: false,
    coldHardy: false,
    storage: ['dry/dehydrate'],
    storageNotes: 'Save seeds from dried flower heads for next year.',
    lifecycle: 'annual',
  },
  {
    name: 'Nasturtium',
    category: 'flower',
    indoorStart: null,
    transplant: null,
    directSow: [19, 23],
    harvestStart: 24,
    harvestEnd: 38,
    daysToMaturity: [40, 60],
    companions: ['Zucchini', 'Cucumber', 'Beans (Bush)', 'Cabbage'],
    avoid: [],
    notes: 'Trap crop for aphids. Edible flowers and leaves (peppery). Trails nicely.',
    successionPlanting: false,
    coldHardy: false,
    storage: ['eat fresh only', 'pickle/ferment'],
    storageNotes: 'Eat flowers and leaves fresh. Pickle seed pods as "poor man\'s capers."',
    lifecycle: 'annual',
  },
  {
    name: 'Borage',
    category: 'flower',
    indoorStart: null,
    transplant: null,
    directSow: [16, 22],
    harvestStart: 22,
    harvestEnd: 36,
    daysToMaturity: [50, 65],
    companions: ['Tomato', 'Strawberry', 'Zucchini'],
    avoid: [],
    notes: 'Excellent pollinator magnet. Edible blue flowers. Self-seeds freely.',
    successionPlanting: false,
    coldHardy: true,
    storage: ['eat fresh only'],
    storageNotes: 'Use flowers fresh in salads and drinks. Leaves can be used like cucumber.',
    lifecycle: 'annual',
  },
];

// Companion planting groups — classic combinations
export const companionGroups = [
  {
    name: 'Three Sisters',
    plants: ['Corn', 'Beans (Pole)', 'Winter Squash'],
    description: 'Native American classic. Corn provides structure for beans, beans fix nitrogen, squash shades soil.',
  },
  {
    name: 'Tomato & Friends',
    plants: ['Tomato', 'Basil', 'Carrot', 'Parsley', 'Marigold'],
    description: 'Basil repels pests and may improve tomato flavor. Carrots loosen soil. Marigolds deter nematodes.',
  },
  {
    name: 'Salsa Garden',
    plants: ['Tomato', 'Pepper (Hot)', 'Cilantro', 'Onion', 'Tomatillo'],
    description: 'Everything you need for fresh salsa and salsa verde. Plant cilantro in succession.',
  },
  {
    name: 'Cool Season Greens',
    plants: ['Lettuce', 'Spinach', 'Radish', 'Peas'],
    description: 'Early spring planting. Radishes mark rows and break soil for others.',
  },
  {
    name: 'Brassica Bed',
    plants: ['Broccoli', 'Cauliflower', 'Cabbage', 'Kale', 'Brussels Sprouts'],
    description: 'Group brassicas together for easier pest management (row cover).',
  },
  {
    name: 'Root Cellar',
    plants: ['Carrot', 'Beet', 'Onion', 'Potato', 'Garlic'],
    description: 'Great storage crops. Plant these for winter keeping.',
  },
  {
    name: 'Pollinator Strip',
    plants: ['Sunflower', 'Borage', 'Marigold', 'Nasturtium'],
    description: 'Plant along garden edges to attract bees, butterflies, and beneficial insects.',
  },
  {
    name: 'Perennial Patch',
    plants: ['Asparagus', 'Rhubarb', 'Strawberry', 'Chives', 'Thyme'],
    description: 'Plant once, harvest for years. Dedicate a permanent bed for these.',
  },
  {
    name: 'Pizza Garden',
    plants: ['Tomato', 'Basil', 'Oregano', 'Pepper (Bell)', 'Onion'],
    description: 'Everything for homemade pizza. Basil and oregano thrive alongside tomatoes and peppers.',
  },
  {
    name: 'Stir Fry Garden',
    plants: ['Peas', 'Cabbage', 'Green Onion', 'Pepper (Bell)', 'Carrot'],
    description: 'Quick-cook veggies that grow well together. Add garlic and ginger from the store.',
  },
  {
    name: 'Pickle Pantry',
    plants: ['Cucumber', 'Dill', 'Beet', 'Beans (Bush)', 'Radish'],
    description: 'Grow it, pickle it, shelf it. Dill and cucumber are a classic pairing in garden and jar.',
  },
  {
    name: 'Taco Night',
    plants: ['Pepper (Hot)', 'Cilantro', 'Tomato', 'Onion', 'Lettuce'],
    description: 'Fresh taco toppings straight from the garden. Succession sow cilantro to keep it coming.',
  },
  {
    name: 'Soup & Stew',
    plants: ['Potato', 'Carrot', 'Celery', 'Onion', 'Leek'],
    description: 'Hearty root veggies and aromatics. Most store well for winter soups.',
  },
  {
    name: 'Shade Helpers',
    plants: ['Corn', 'Sunflower', 'Lettuce', 'Spinach'],
    description: 'Tall crops shade cool-season greens, extending their harvest before they bolt in heat.',
  },
];

export const WEEK_LABELS = [
  '', 'Jan 1', 'Jan 8', 'Jan 15', 'Jan 22',
  'Feb 1', 'Feb 8', 'Feb 15', 'Feb 22',
  'Mar 1', 'Mar 8', 'Mar 15', 'Mar 22', 'Mar 29',
  'Apr 5', 'Apr 12', 'Apr 19', 'Apr 26',
  'May 3', 'May 10', 'May 17', 'May 24', 'May 31',
  'Jun 7', 'Jun 14', 'Jun 21', 'Jun 28',
  'Jul 5', 'Jul 12', 'Jul 19', 'Jul 26',
  'Aug 2', 'Aug 9', 'Aug 16', 'Aug 23', 'Aug 30',
  'Sep 6', 'Sep 13', 'Sep 20', 'Sep 27',
  'Oct 4', 'Oct 11', 'Oct 18', 'Oct 25',
  'Nov 1', 'Nov 8', 'Nov 15', 'Nov 22', 'Nov 29',
  'Dec 6', 'Dec 13', 'Dec 20', 'Dec 27',
];

export const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const MONTH_WEEKS: [number, number][] = [
  [1, 4], [5, 8], [9, 13], [14, 17], [18, 22], [23, 26],
  [27, 30], [31, 35], [36, 39], [40, 43], [44, 48], [49, 52],
];

export const LAST_FROST_WEEK = 19;  // ~May 10
export const FIRST_FROST_WEEK = 40; // ~Oct 5
